import React, { useEffect, useMemo, useState } from 'react';
import PaginationControls from '@/components/PaginationControls';
import { useClientErrors } from '@/services/api';
import useDebounce from '@/hooks/useDebounce';

const formatTimestamp = (value?: string | null) => {
  if (!value) return '--';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString('zh-TW', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const ClientErrors: React.FC = () => {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 400);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const filters = useMemo(
    () => ({
      query: debouncedSearch.trim() || undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      page,
      pageSize,
    }),
    [debouncedSearch, startDate, endDate, page, pageSize],
  );

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, startDate, endDate]);

  const {
    data: clientErrorsPayload,
    isLoading,
    error,
  } = useClientErrors(filters);
  const logs = clientErrorsPayload?.value ?? [];
  const meta =
    clientErrorsPayload?.meta ?? ({ page, pageSize, total: 0, totalPages: 1, hasMore: false } as const);
  const totalPages = meta.totalPages ?? Math.max(1, Math.ceil((meta.total ?? 0) / (meta.pageSize ?? pageSize)));
  const startIndex = logs.length ? (meta.page - 1) * meta.pageSize + 1 : 0;
  const endIndex = logs.length ? startIndex + logs.length - 1 : 0;
  const severityCounts = clientErrorsPayload?.stats?.severityCounts ?? {};
  const severityTiles = [
    { key: 'critical', label: 'Critical', tone: 'text-rose-600', bg: 'bg-rose-50' },
    { key: 'error', label: 'Error', tone: 'text-red-600', bg: 'bg-red-50' },
    { key: 'warning', label: 'Warning', tone: 'text-amber-600', bg: 'bg-amber-50' },
    { key: 'info', label: 'Info', tone: 'text-slate-600', bg: 'bg-slate-100' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="section-title">系統診斷</p>
          <h1 className="text-2xl font-semibold text-slate-900">Client Error Logs</h1>
          <p className="text-sm text-slate-500">檢視前端 ErrorBoundary 回報的最新異常，快速連回 Sentry。</p>
        </div>
      </div>

      <section className="glass-panel p-6 space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-xs font-semibold text-slate-500">
            起始
            <input
              type="date"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
              className="mt-1 rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-brand-blue focus:ring-brand-blue/20"
            />
          </label>
          <label className="text-xs font-semibold text-slate-500">
            結束
            <input
              type="date"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
              className="mt-1 rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-brand-blue focus:ring-brand-blue/20"
            />
          </label>
          <div className="flex flex-1 flex-col text-xs">
            <span className="font-semibold text-slate-500">搜尋訊息 / URL / User Agent</span>
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="例：TypeError 或 /patients/12"
              className="mt-1 rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-brand-blue focus:ring-brand-blue/20"
          />
        </div>

        <div className="grid gap-3 md:grid-cols-4">
          {severityTiles.map((tile) => (
            <div key={tile.key} className={`rounded-2xl border border-slate-100 px-4 py-3 ${tile.bg}`}>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{tile.label}</p>
              <p className={`text-2xl font-bold ${tile.tone}`}>
                {severityCounts[tile.key] ?? 0}
              </p>
            </div>
          ))}
        </div>
        </div>

        {error && (
          <p className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700" role="alert">
            無法載入錯誤記錄：{error instanceof Error ? error.message : '未知錯誤'}
          </p>
        )}

        <div className="rounded-2xl border border-slate-100 bg-white">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100 text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-slate-600">訊息</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-600">Severity</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-600">URL</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-600">User Agent</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-600">時間</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-600">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {isLoading ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-6 text-center text-slate-400">
                      載入中…
                    </td>
                  </tr>
                ) : logs.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-6 text-center text-slate-400">
                      目前沒有符合條件的錯誤。
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => (
                    <tr key={log.id} className="align-top">
                      <td className="px-4 py-3">
                        <p className="font-semibold text-slate-900">{log.message}</p>
                        {log.stack && (
                          <pre className="mt-1 max-h-24 overflow-hidden rounded bg-slate-900/90 p-2 text-[11px] text-amber-100">
                            {log.stack.slice(0, 300)}
                            {log.stack.length > 300 ? '…' : ''}
                          </pre>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">
                        <span
                          className={`inline-flex rounded-full px-3 py-0.5 text-[11px] font-semibold ${
                            log.severity === 'critical'
                              ? 'bg-rose-100 text-rose-700'
                              : log.severity === 'error'
                                ? 'bg-red-100 text-red-700'
                                : log.severity === 'warning'
                                  ? 'bg-amber-100 text-amber-700'
                                  : 'bg-slate-100 text-slate-600'
                          }`}
                        >
                          {log.severity?.toUpperCase() ?? 'INFO'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">
                        {log.url ? (
                          <a href={log.url} className="text-brand-blue underline" target="_blank" rel="noreferrer">
                            {log.url}
                          </a>
                        ) : (
                          '--'
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500 break-all">{log.userAgent ?? '--'}</td>
                      <td className="px-4 py-3 text-xs text-slate-500">{formatTimestamp(log.createdAt)}</td>
                      <td className="px-4 py-3 text-xs text-slate-500">
                        {log.sentrySearchUrl ? (
                          <a
                            href={log.sentrySearchUrl}
                            className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                            target="_blank"
                            rel="noreferrer"
                          >
                            在 Sentry 搜尋
                          </a>
                        ) : (
                          <span className="text-slate-400">未配置 Sentry</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {meta.total > 0 && (
            <PaginationControls
              page={meta.page}
              perPage={meta.pageSize}
              total={meta.total}
              totalPages={totalPages}
              canNext={meta.hasMore}
              canPrev={meta.page > 1}
              startIndex={startIndex}
              endIndex={endIndex}
              onPageChange={setPage}
              onPerPageChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
            />
          )}
        </div>
      </section>
    </div>
  );
};

export default ClientErrors;
